import { Room, RoomEvent, Track } from "livekit-client";

class Livekit {
    constructor() {
        this.room = null;
        this.is_connected = false;

        this.callbacks = {
            on_connected: null,
            on_disconnected: null,
            on_error: null,
            on_chat_message: null,
            on_stop_exam: null,
        };
    }

    init(options = {}) {
        if (options.callbacks) {
            this.callbacks.on_connected =
                options.callbacks.on_connected || options.callbacks.onConnected;

            this.callbacks.on_disconnected =
                options.callbacks.on_disconnected ||
                options.callbacks.onDisconnected;

            this.callbacks.on_error =
                options.callbacks.on_error || options.callbacks.onError;

            this.callbacks.on_chat_message =
                options.callbacks.on_chat_message ||
                options.callbacks.onChatMessage;

            this.callbacks.on_stop_exam =
                options.callbacks.on_stop_exam ||
                options.callbacks.onStopExam;
        }
    }

    async start(camera_stream, screen_stream, token, server_url) {
        try {
            if (!token || !server_url) {
                throw new Error("Token and server_url are required");
            }

            this.room = new Room({
                adaptiveStream: false,
                dynacast: true,

                publishDefaults: {
                    videoCodec: "h264",
                },
            });

            // --------------------------------------------------
            // EVENTS
            // --------------------------------------------------

            this.room.on(RoomEvent.Connected, () => {
                this.is_connected = true;

                console.log("[LiveKit] Connected");

                this.callbacks.on_connected?.();
            });

            this.room.on(RoomEvent.Disconnected, () => {
                this.is_connected = false;

                console.log("[LiveKit] Disconnected");

                this.callbacks.on_disconnected?.();
            });

            this.room.on(
                RoomEvent.DataReceived,
                (payload, participant, kind, topic) => {
                    try {
                        const decoder = new TextDecoder();

                        const strData = decoder.decode(payload);

                        const data = JSON.parse(strData);

                        if (data.type === "CHAT_MESSAGE") {
                            this.callbacks.on_chat_message?.(data.data);
                        } else if (data.type === "STOP_EXAM") {
                            this.callbacks.on_stop_exam?.(data.data);
                        }
                    } catch (err) {
                        console.warn(
                            "[LiveKit] Failed to parse data message:",
                            err,
                        );
                    }
                },
            );

            // --------------------------------------------------
            // CONNECT
            // --------------------------------------------------

            await this.room.connect(server_url, token);

            // --------------------------------------------------
            // CAMERA
            // --------------------------------------------------

            if (camera_stream) {
                const video_track = camera_stream.getVideoTracks()[0];

                if (video_track) {
                    await this.room.localParticipant.publishTrack(
                        video_track,
                        {
                            name: "camera",

                            source: Track.Source.Camera,

                            simulcast: true,

                            videoEncoding: {
                                maxBitrate: 220000,
                                maxFramerate: 15,
                            },

                            videoSimulcastLayers: [
                                {
                                    width: 320,
                                    height: 180,

                                    encoding: {
                                        maxBitrate: 60000,
                                        maxFramerate: 10,
                                    },
                                },

                                {
                                    width: 640,
                                    height: 360,

                                    encoding: {
                                        maxBitrate: 140000,
                                        maxFramerate: 15,
                                    },
                                },

                                {
                                    width: 1280,
                                    height: 720,

                                    encoding: {
                                        maxBitrate: 220000,
                                        maxFramerate: 15,
                                    },
                                },
                            ],
                        },
                    );

                    console.log("[LiveKit] Camera track published");
                }

                // AUDIO

                const audio_track = camera_stream.getAudioTracks()[0];

                if (audio_track) {
                    await this.room.localParticipant.publishTrack(
                        audio_track,
                        {
                            name: "microphone",

                            source: Track.Source.Microphone,
                        },
                    );

                    console.log("[LiveKit] Audio track published");
                }
            }

            // --------------------------------------------------
            // SCREEN SHARE
            // --------------------------------------------------

            if (screen_stream) {
                const screen_video_track =
                    screen_stream.getVideoTracks()[0];

                if (screen_video_track) {
                    screen_video_track.contentHint = "detail";

                    await this.room.localParticipant.publishTrack(
                        screen_video_track,
                        {
                            name: "screen",

                            source: Track.Source.ScreenShare,

                            simulcast: true,

                            videoEncoding: {
                                maxBitrate: 240000,
                                maxFramerate: 10,
                            },

                            videoSimulcastLayers: [
                                {
                                    width: 640,
                                    height: 360,

                                    encoding: {
                                        maxBitrate: 80000,
                                        maxFramerate: 8,
                                    },
                                },

                                {
                                    width: 960,
                                    height: 540,

                                    encoding: {
                                        maxBitrate: 160000,
                                        maxFramerate: 10,
                                    },
                                },

                                {
                                    width: 1280,
                                    height: 720,

                                    encoding: {
                                        maxBitrate: 240000,
                                        maxFramerate: 10,
                                    },
                                },
                            ],
                        },
                    );

                    console.log("[LiveKit] Screen track published");
                }
            }
        } catch (error) {
            console.error("[LiveKit] Failed to start:", error);

            this.callbacks.on_error?.(error);

            throw error;
        }
    }

    stop() {
        if (this.room) {
            this.room.disconnect();

            this.room = null;

            this.is_connected = false;

            console.log("[LiveKit] Stopped");
        }
    }

    async republishCamera(camera_stream) {
        if (!this.room || !this.is_connected) {
            console.warn("[LiveKit] Cannot republish - not connected");

            return;
        }

        try {
            const local = this.room.localParticipant;

            const tracks_to_unpublish = [];

            local.trackPublications.forEach((pub) => {
                if (
                    pub.source === Track.Source.Camera ||
                    pub.source === Track.Source.Microphone
                ) {
                    tracks_to_unpublish.push(pub);
                }
            });

            for (const pub of tracks_to_unpublish) {
                if (pub.track) {
                    await local.unpublishTrack(pub.track);

                    console.log(
                        `[LiveKit] Unpublished ${pub.source} track`,
                    );
                }
            }

            if (camera_stream) {
                const video_track = camera_stream.getVideoTracks()[0];

                if (video_track) {
                    await local.publishTrack(video_track, {
                        name: "camera",

                        source: Track.Source.Camera,

                        simulcast: true,

                        videoEncoding: {
                            maxBitrate: 220000,
                            maxFramerate: 15,
                        },

                        videoSimulcastLayers: [
                            {
                                width: 320,
                                height: 180,

                                encoding: {
                                    maxBitrate: 60000,
                                    maxFramerate: 10,
                                },
                            },

                            {
                                width: 640,
                                height: 360,

                                encoding: {
                                    maxBitrate: 140000,
                                    maxFramerate: 15,
                                },
                            },

                            {
                                width: 1280,
                                height: 720,

                                encoding: {
                                    maxBitrate: 220000,
                                    maxFramerate: 15,
                                },
                            },
                        ],
                    });

                    console.log(
                        "[LiveKit] Camera track republished",
                    );
                }

                const audio_track = camera_stream.getAudioTracks()[0];

                if (audio_track) {
                    await local.publishTrack(audio_track, {
                        name: "microphone",

                        source: Track.Source.Microphone,
                    });

                    console.log(
                        "[LiveKit] Audio track republished",
                    );
                }
            }
        } catch (error) {
            console.error(
                "[LiveKit] Failed to republish camera:",
                error,
            );

            this.callbacks.on_error?.(error);
        }
    }

    async republishScreen(screen_stream) {
        if (!this.room || !this.is_connected) {
            console.warn("[LiveKit] Cannot republish - not connected");

            return;
        }

        try {
            const local = this.room.localParticipant;

            const tracks_to_unpublish = [];

            local.trackPublications.forEach((pub) => {
                if (pub.source === Track.Source.ScreenShare) {
                    tracks_to_unpublish.push(pub);
                }
            });

            for (const pub of tracks_to_unpublish) {
                if (pub.track) {
                    await local.unpublishTrack(pub.track);

                    console.log(
                        "[LiveKit] Unpublished screen track",
                    );
                }
            }

            if (screen_stream) {
                const screen_video_track =
                    screen_stream.getVideoTracks()[0];

                if (screen_video_track) {
                    screen_video_track.contentHint = "detail";

                    await local.publishTrack(
                        screen_video_track,
                        {
                            name: "screen",

                            source: Track.Source.ScreenShare,

                            simulcast: true,

                            videoEncoding: {
                                maxBitrate: 240000,
                                maxFramerate: 10,
                            },

                            videoSimulcastLayers: [
                                {
                                    width: 640,
                                    height: 360,

                                    encoding: {
                                        maxBitrate: 80000,
                                        maxFramerate: 8,
                                    },
                                },

                                {
                                    width: 960,
                                    height: 540,

                                    encoding: {
                                        maxBitrate: 160000,
                                        maxFramerate: 10,
                                    },
                                },

                                {
                                    width: 1280,
                                    height: 720,

                                    encoding: {
                                        maxBitrate: 240000,
                                        maxFramerate: 10,
                                    },
                                },
                            ],
                        },
                    );

                    console.log(
                        "[LiveKit] Screen track republished",
                    );
                }
            }
        } catch (error) {
            console.error(
                "[LiveKit] Failed to republish screen:",
                error,
            );

            this.callbacks.on_error?.(error);
        }
    }
}

export default new Livekit();